import { trackCtaClick, trackCtaFocus, type CtaType } from "@/lib/analytics";
import { validateSectionPosition } from "@/lib/section-visibility";

/** Offset for the sticky 64 px header plus a small breathing gap. */
export const DEFAULT_HEADER_OFFSET = 80;

export type SectionJumpOptions = {
  /** DOM id of the section to jump to, without the leading "#". */
  targetId: string;
  /** Stable label of the clicked jump link, e.g. "Or book a meeting directly". */
  ctaLabel: string;
  /** CTA classification of the jump link. */
  ctaType?: CtaType;
  /** Label used for the follow-up cta_focus event, e.g. "Booking section". */
  focusLabel: string;
  /** Viewport Y coordinate of the click, when available. */
  clickY?: number;
  /** Sticky header offset in px. */
  headerOffset?: number;
};

/**
 * Shared behaviour for in-page jump links: consistent cta_click tracking,
 * a header-aware and clamped smooth scroll (respecting prefers-reduced-motion),
 * focus move to the target and a cta_focus event carrying the effective
 * `scrollY` offset, so analytics can derive which section was actually shown.
 *
 * Returns the effective scroll offset, or null when the target is missing.
 */
export function jumpToSection({
  targetId,
  ctaLabel,
  ctaType = "inline_link",
  focusLabel,
  clickY,
  headerOffset = DEFAULT_HEADER_OFFSET,
}: SectionJumpOptions): { element: HTMLElement; top: number; documentY: number | null } | null {
  const hash = `#${targetId}`;
  trackCtaClick(ctaLabel, ctaType, hash, `${ctaType} ${hash}`, clickY);

  if (typeof document === "undefined") return null;
  const element = document.getElementById(targetId);
  if (!element) return null;

  const documentHeight = document.documentElement.scrollHeight;
  const viewportHeight = window.innerHeight;
  const maxScroll = Math.max(0, documentHeight - viewportHeight);
  const top = Math.max(
    0,
    Math.min(maxScroll, element.getBoundingClientRect().top + window.scrollY - headerOffset),
  );

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top, behavior: prefersReducedMotion ? "auto" : "smooth" });

  if (!element.hasAttribute("tabindex")) element.setAttribute("tabindex", "-1");
  element.focus({ preventScroll: true });

  const scrollY = Math.round(top);
  // Only send coordinates that actually describe a section on this page.
  const position = validateSectionPosition(`jump ${hash}`, {
    ...(typeof clickY === "number" ? { clickY } : {}),
    scrollY,
    viewportHeight,
    documentHeight,
  });

  if (position.valid) {
    trackCtaFocus(focusLabel, hash, "focus after jump link", scrollY);
  } else {
    trackCtaFocus(focusLabel, hash, "focus after jump link (invalid offset)");
  }

  return { element, top, documentY: position.documentY };
}

